const workerUrl = "/messenger-push-worker.js"
const disabledKey = "messenger.push.disabled"

function rememberDisabled(disabled: boolean): void {
    try {
        if (disabled) window.localStorage.setItem(disabledKey, "1")
        else window.localStorage.removeItem(disabledKey)
    } catch {
        // Push must also work when browser storage is unavailable.
    }
}

function supported(): boolean {
    return (
        window.isSecureContext &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    )
}

async function pushConfig(): Promise<{ publicKey: string; csrfToken: string }> {
    const response = await fetch("/messenger/push/config", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        cache: "no-store",
    })
    if (!response.ok) throw new Error("Unable to load push settings")
    return response.json()
}

async function save(subscription: PushSubscription, csrfToken: string): Promise<void> {
    const response = await fetch("/messenger/push/subscriptions", {
        method: "POST",
        credentials: "same-origin",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": csrfToken,
        },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
    })
    if (!response.ok) throw new Error("Unable to save push subscription")
}

function decodeKey(key: string): Uint8Array {
    const bytes = atob(key.replace(/-/g, "+").replace(/_/g, "/"))
    return Uint8Array.from(bytes, (character) => character.charCodeAt(0))
}

export async function enableMessengerPush(): Promise<boolean> {
    if (!supported() || Notification.permission !== "granted") return false
    const config = await pushConfig()
    const registration = await navigator.serviceWorker.register(workerUrl, { scope: "/" })
    const worker = registration.installing || registration.waiting
    if (!worker && !registration.active) throw new Error("Push worker is not available")
    if (worker && worker.state !== "activated") {
        await new Promise<void>((resolve, reject) => {
            const timeout = window.setTimeout(() => {
                worker.removeEventListener("statechange", changed)
                reject(new Error("Push worker activation timed out"))
            }, 15000)
            const changed = () => {
                if (worker.state === "activated" || worker.state === "redundant") {
                    window.clearTimeout(timeout)
                    worker.removeEventListener("statechange", changed)
                    if (worker.state === "activated") resolve()
                    else reject(new Error("Push worker failed to activate"))
                }
            }
            worker.addEventListener("statechange", changed)
            changed()
        })
    }
    let subscription = await registration.pushManager.getSubscription()
    const publicKey = decodeKey(config.publicKey)
    if (subscription) {
        const currentKey = subscription.options.applicationServerKey
        if (
            !currentKey ||
            currentKey.byteLength !== publicKey.byteLength ||
            new Uint8Array(currentKey).some((byte, index) => byte !== publicKey[index])
        ) {
            if (!(await subscription.unsubscribe()))
                throw new Error("Unable to renew push subscription")
            subscription = null
        }
    }
    subscription =
        subscription ||
        (await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: publicKey,
        }))
    await save(subscription, config.csrfToken)
    rememberDisabled(false)
    return true
}

export async function restoreMessengerPush(): Promise<boolean> {
    if (!supported() || Notification.permission !== "granted") return false
    try {
        if (window.localStorage.getItem(disabledKey) === "1") return false
    } catch {
        // An existing browser permission is enough to reconnect push.
    }
    // Existing users already granted notifications before push was introduced.
    // Connect them too, unless they explicitly switched notifications off.
    return enableMessengerPush()
}

export async function disableMessengerPush(): Promise<void> {
    if (!supported()) {
        rememberDisabled(true)
        return
    }
    const registration = await navigator.serviceWorker.getRegistration("/")
    const subscription = await registration?.pushManager.getSubscription()
    if (!subscription) {
        rememberDisabled(true)
        return
    }
    const endpoint = subscription.endpoint
    // Stop delivery in this browser even if the portal is temporarily unavailable.
    if (!(await subscription.unsubscribe())) throw new Error("Unable to disable push notifications")
    rememberDisabled(true)
    try {
        const config = await pushConfig()
        await fetch("/messenger/push/subscriptions", {
            method: "DELETE",
            credentials: "same-origin",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": config.csrfToken,
            },
            body: JSON.stringify({ endpoint }),
        })
    } catch {
        // The sender removes expired endpoints after the browser unsubscribes.
    }
}
